import { User } from '@/client/users';
import { UserOrigin } from '@/client/users/types/origins.enum';
import { LoadingStatuses } from '@/common/constants';
import { AppButton, AppSplitButton } from '@/ui/buttons';
import { WarningTriangleIcon } from '@/utils/helpers/icons.helper';
import { Dialog } from 'primereact/dialog';
import { MenuItem } from 'primereact/menuitem';
import { Tooltip } from 'primereact/tooltip';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

const BulkActionBar = styled.div<{ $centerLeft: number | null }>`
  position: fixed;
  bottom: var(--default-padding);
  left: ${(p) => (p.$centerLeft !== null ? `${p.$centerLeft}px` : '50%')};
  transform: translateX(-50%);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--medium-padding);

  padding: 20px 30px;
  min-width: 360px;
  border-radius: var(--box-border-radius);
  border: 1px solid var(--gray-dark);
  background: var(--white-main);
`;

const BulkActionBarCount = styled.span`
  color: var(--black-main);
  font-size: var(--xsmall-font-size);
  font-weight: 500;
  flex-shrink: 0;
`;

const DeselectAllButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  padding-bottom: var(--xxsmall-padding);
  cursor: pointer;
  color: var(--black-main);
  font-size: var(--small-font-size);
  font-weight: 500;
  flex-shrink: 0;
  border-bottom: 2px solid var(--gray-buttom-border);
  transition: border-bottom 0.3s ease-in-out;

  &:hover {
    border-bottom: 2px solid var(--red-main);
  }
`;

const StyledDialog = styled(Dialog)`
  width: 530px;
`;

const BulkDeleteModalMessage = styled.p`
  margin: 0;
  margin-top: var(--xxsmall-padding);
  font-size: var(--small-font-size);
  font-weight: 400;
  color: var(--black-main);
  line-height: 1.5;
`;

const BulkDeleteModalWarning = styled.div`
  display: flex;
  align-items: flex-start;
  gap: var(--small-padding);
  margin-top: var(--semi-big-padding);
  padding: var(--medium-padding) var(--default-padding);
  padding-left: var(--small-padding);
  background: var(--yellow-warning);
  border-radius: var(--box-border-radius);

  svg {
    flex-shrink: 0;
  }

  p {
    font-size: var(--small-font-size);
    font-weight: 500;
    color: var(--black-main);
    line-height: var(--small-line-height);
  }
`;

const BulkDeleteModalFooter = styled.div`
  display: flex;
  gap: var(--small-padding);
  margin-top: var(--semi-big-padding);
`;

type UserBulkDeleteControlsProps = {
  canBulkDelete: boolean;
  selectedUsers: User[];
  bulkDeleting: boolean;
  showModal: boolean;
  setShowModal: (show: boolean) => void;
  onConfirm: (users: User[]) => void;
  onDeselectAll: () => void;
  pendingDeletionMode?: boolean;
  canBulkRestore?: boolean;
  canBulkPermanentDelete?: boolean;
  restoreDisabled?: boolean;
  restoreDisabledTooltip?: string;
  onRestore?: () => void;
  onPermanentDelete?: () => void;
};

export const UserBulkDeleteControls: React.FC<UserBulkDeleteControlsProps> = ({
  canBulkDelete,
  selectedUsers,
  bulkDeleting,
  showModal,
  setShowModal,
  onConfirm,
  onDeselectAll,
  pendingDeletionMode = false,
  canBulkRestore = false,
  canBulkPermanentDelete = false,
  restoreDisabled = false,
  restoreDisabledTooltip,
  onRestore,
  onPermanentDelete,
}) => {
  const { t } = useTranslation();

  const [contentCenterLeft, setContentCenterLeft] = useState<number | null>(
    null,
  );

  useEffect(() => {
    const updateBarPosition = () => {
      const contentEl = document.querySelector(
        '.with-topbar .content',
      ) as HTMLElement | null;
      if (contentEl) {
        const rect = contentEl.getBoundingClientRect();
        setContentCenterLeft(rect.left + rect.width / 2);
      } else {
        setContentCenterLeft(null);
      }
    };

    updateBarPosition();
    window.addEventListener('resize', updateBarPosition);
    return () => window.removeEventListener('resize', updateBarPosition);
  }, []);

  const selectedCount = selectedUsers.length;
  const hasActions = pendingDeletionMode
    ? canBulkRestore || canBulkPermanentDelete
    : canBulkDelete;
  const hasSelection = hasActions && selectedCount > 0;

  const hasAdSyncUsersInSelection = selectedUsers.some(
    (u) => u.origin === UserOrigin.AZURE_AD,
  );

  const restoreMenuItem: MenuItem = {
    label: t('user.bulk.restoreUsers', { count: selectedCount }),
    icon: 'pi pi-replay',
    disabled: restoreDisabled,
    command: onRestore,
    template: restoreDisabled
      ? (item: any) => (
          <>
            <div
              id="bulk-restore-disabled"
              className="p-menuitem-content"
              style={{ pointerEvents: 'auto' }}
            >
              <a
                className="p-menuitem-link p-disabled"
                style={{ opacity: 0.5 }}
              >
                <span className="p-menuitem-icon pi pi-replay" />
                <span className="p-menuitem-text">{item.label}</span>
              </a>
            </div>
            <Tooltip
              target="#bulk-restore-disabled"
              content={restoreDisabledTooltip}
              position="top"
            />
          </>
        )
      : undefined,
  };

  const permanentDeleteMenuItem: MenuItem = {
    label: t('user.bulk.deletePermanently', { count: selectedCount }),
    icon: 'pi pi-trash',
    className: 'p-menuitem-danger',
    command: onPermanentDelete,
  };

  const renderPendingDeletionActions = () => {
    if (canBulkRestore && canBulkPermanentDelete) {
      return (
        <AppSplitButton
          menuId="bulk-pending-deletion-menu"
          items={[restoreMenuItem, permanentDeleteMenuItem]}
          menuAriaLabel={t('user.bulk.moreActions')}
          label={t('user.bulk.deletePermanently', { count: selectedCount })}
          severity="danger"
          state={bulkDeleting ? LoadingStatuses.LOADING : LoadingStatuses.IDLE}
          isDisabled={bulkDeleting}
          onClick={onPermanentDelete}
        />
      );
    }

    if (canBulkRestore) {
      return (
        <AppButton
          label={t('user.bulk.restoreUsers', { count: selectedCount })}
          icon="pi pi-replay"
          iconPos="right"
          isDisabled={bulkDeleting || restoreDisabled}
          tooltip={restoreDisabled ? restoreDisabledTooltip : undefined}
          onClick={onRestore}
        />
      );
    }

    return (
      <AppButton
        label={t('user.bulk.deletePermanently', { count: selectedCount })}
        icon="pi pi-trash"
        iconPos="right"
        severity="danger"
        state={bulkDeleting ? LoadingStatuses.LOADING : LoadingStatuses.IDLE}
        isDisabled={bulkDeleting}
        onClick={onPermanentDelete}
      />
    );
  };

  return (
    <>
      {!pendingDeletionMode && (
        <StyledDialog
          visible={showModal}
          onHide={() => setShowModal(false)}
          header={t('dialog.delete.bulk.message', { count: selectedCount })}
          className="v2"
        >
          <BulkDeleteModalMessage>
            {t('dialog.delete.irreversible')}
          </BulkDeleteModalMessage>
          {hasAdSyncUsersInSelection && (
            <BulkDeleteModalWarning>
              <WarningTriangleIcon />
              <p className="m-0">{t('dialog.delete.bulk.adSyncWarning')}</p>
            </BulkDeleteModalWarning>
          )}
          <BulkDeleteModalFooter>
            <AppButton
              label={t('user.bulk.deleteUsers', { count: selectedCount })}
              severity="danger"
              state={
                bulkDeleting ? LoadingStatuses.LOADING : LoadingStatuses.IDLE
              }
              isDisabled={bulkDeleting}
              onClick={() => onConfirm(selectedUsers)}
            />
            <AppButton
              type="outlined"
              label={t('button.cancel')}
              severity="secondary"
              onClick={() => setShowModal(false)}
            />
          </BulkDeleteModalFooter>
        </StyledDialog>
      )}

      {hasSelection && (
        <BulkActionBar $centerLeft={contentCenterLeft}>
          <BulkActionBarCount>
            {t('user.bulk.selectedCount', { count: selectedCount })}
          </BulkActionBarCount>
          {pendingDeletionMode ? (
            renderPendingDeletionActions()
          ) : (
            <AppButton
              label={t('user.bulk.deleteUsers', {
                count: selectedCount,
              })}
              icon="pi pi-trash"
              iconPos="right"
              severity="danger"
              state={
                bulkDeleting ? LoadingStatuses.LOADING : LoadingStatuses.IDLE
              }
              isDisabled={bulkDeleting}
              onClick={() => setShowModal(true)}
            />
          )}
          <DeselectAllButton type="button" onClick={onDeselectAll}>
            {t('user.bulk.deselectAll')}
          </DeselectAllButton>
        </BulkActionBar>
      )}
    </>
  );
};
